/**
 * [CFD-2026-05-12] Unified margin/PnL math — JS mirror of `/app/backend/finance_math.py`.
 *
 * Use these helpers everywhere prices and lots are involved instead of the
 * legacy `volume × 100_000 × price / leverage` formula, which only worked for
 * forex pairs where USD is the QUOTE currency (EURUSD, GBPUSD, etc.).
 *
 * Examples:
 *   margin('GBPJPY', 0.01, 213.5, 100) ≈ 12.6  (was buggy: 2135)
 *   margin('EURUSD', 0.01, 1.084, 100) ≈ 10.84
 *   margin('BTCUSD', 0.01, 67420, 20)  ≈ 33.71
 *   margin('XAUUSD', 0.01, 2318.5, 50) ≈ 46.37
 */

// Approximate currency → USD rates for cross-pairs.
const CURR_USD = {
  USD: 1.0,
  EUR: 1.08,
  GBP: 1.26,
  JPY: 0.0065,
  CHF: 1.10,
  AUD: 0.65,
  NZD: 0.60,
  CAD: 0.73,
};

// Contract size per 1 lot for non-forex symbols.
const CONTRACT_SIZE = {
  BTCUSD: 1, ETHUSD: 1, BNBUSD: 1, XRPUSD: 1, SOLUSD: 1, ADAUSD: 1,
  DOTUSD: 1, LINKUSD: 1, AVAXUSD: 1, LTCUSD: 1, MATICUSD: 1, ATOMUSD: 1,
  XAUUSD: 100, XAGUSD: 5000,
  USOIL: 1000, UKOIL: 1000,
  NATGAS: 10000, COPPER: 25000,
  CORN: 5000, WHEAT: 5000,
  US30: 1, SPX500: 1, NAS100: 1, GER40: 1,
  UK100: 1, FRA40: 1, JPN225: 1, HK50: 1,
  AAPL: 1, MSFT: 1, GOOGL: 1, AMZN: 1, TSLA: 1, META: 1, NVDA: 1, AMD: 1,
};

export const isForex = (symbol) => {
  const s = (symbol || '').toUpperCase();
  if (s.length !== 6 || !/^[A-Z]{6}$/.test(s)) return false;
  return CURR_USD[s.slice(0, 3)] !== undefined && CURR_USD[s.slice(3, 6)] !== undefined;
};

export const currToUsd = (curr) => CURR_USD[(curr || '').toUpperCase()] ?? 1.0;
export const contractSize = (symbol) => CONTRACT_SIZE[(symbol || '').toUpperCase()] ?? 1;

export const calcMargin = (symbol, volume, price, leverage) => {
  const lev = Math.max(parseInt(leverage, 10) || 1, 1);
  const vol = parseFloat(volume) || 0;
  const px = parseFloat(price) || 0;
  const s = (symbol || '').toUpperCase();
  if (isForex(s)) {
    const base = s.slice(0, 3);
    const quote = s.slice(3, 6);
    const notionalBase = vol * 100000;
    let baseUsd;
    if (base === 'USD') baseUsd = 1.0;
    else if (quote === 'USD') baseUsd = px;
    else baseUsd = currToUsd(base);
    return Math.round((notionalBase * baseUsd / lev) * 100) / 100;
  }
  return Math.round((vol * contractSize(s) * px / lev) * 100) / 100;
};

export const calcPnl = (symbol, direction, volume, entry, current) => {
  const vol = parseFloat(volume) || 0;
  const e = parseFloat(entry) || 0;
  const c = parseFloat(current) || 0;
  const diff = direction === 'buy' ? (c - e) : (e - c);
  const s = (symbol || '').toUpperCase();
  if (isForex(s)) {
    const base = s.slice(0, 3);
    const quote = s.slice(3, 6);
    const pnlQuote = vol * 100000 * diff;
    if (quote === 'USD') return Math.round(pnlQuote * 100) / 100;
    if (base === 'USD') return Math.round(pnlQuote / Math.max(c, 1e-9) * 100) / 100;
    return Math.round(pnlQuote * currToUsd(quote) * 100) / 100;
  }
  return Math.round(vol * contractSize(s) * diff * 100) / 100;
};

/**
 * [TP-2026-05-19g] Convert a desired USD profit/loss into the absolute price
 * level that produces it for the given symbol/direction/volume.
 *
 * This is the inverse of `calcPnl` and must stay symmetric with it, otherwise
 * setting TP=$20 will trigger at a price level whose actual realized PnL
 * differs (the bug user reported: $20 TP closed at $24/$30 because the old
 * formula assumed `XXXUSD` for every asset).
 *
 *   targetPriceForUsd("USDJPY","buy",0.1,150.00, 20)  → 150.30   (≈ +$19.97)
 *   targetPriceForUsd("EURUSD","buy",0.1,1.085, 20)   → 1.087    (= +$20.00)
 *   targetPriceForUsd("BTCUSD","buy",0.01,67000, 100) → 77000    (= +$100.00)
 *
 * Returns NaN if inputs invalid.
 */
export const targetPriceForUsd = (symbol, direction, volume, entry, usdTarget) => {
  const vol = parseFloat(volume) || 0;
  const e = parseFloat(entry) || 0;
  const usd = parseFloat(usdTarget) || 0;
  if (vol <= 0 || e <= 0 || !isFinite(usd)) return NaN;
  const s = (symbol || '').toUpperCase();
  let diff;            // price-units change from entry to hit usd PnL (always +)
  if (isForex(s)) {
    const base = s.slice(0, 3);
    const quote = s.slice(3, 6);
    if (quote === 'USD') {
      diff = usd / (vol * 100000);
    } else if (base === 'USD') {
      // pnl_usd = vol*100000*diff/current  →  diff = pnl_usd*current/(vol*100000)
      // We approximate current ≈ entry for SL/TP placement (Δ is small).
      diff = (usd * e) / (vol * 100000);
    } else {
      diff = usd / (currToUsd(quote) * vol * 100000);
    }
  } else {
    const cs = contractSize(s);
    diff = usd / (vol * cs);
  }
  // BUY: positive USD target ⇒ price up; SELL: positive USD target ⇒ price down.
  return direction === 'buy' || direction === 'b'
    ? e + diff
    : e - diff;
};
